/**
 * HotspotHeatmapCard
 *
 * 24h reward heatmap — stündliche Reward-Intensität.
 * Benötigt echte stündliche Reward-Daten (hourlyRewards-Prop, 24 Werte).
 * Ohne Daten wird ein ehrlicher Empty-State gezeigt — kein Math.random().
 */
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

// --- Types ---

type Props = {
  /** Stündliche Reward-Daten (exakt 24 Werte, Index = Stunde 0–23). */
  hourlyRewards?: number[]
}

// --- HeatCell ---

type HeatCellProps = {
  intensity: number // 0-4
  hour: number
  labelNoReward: string
  labelHighActivity: string
  labelActivity: string
}

function HeatCell({ intensity, hour, labelNoReward, labelHighActivity, labelActivity }: HeatCellProps) {
  const cellLabel = intensity === 0 ? labelNoReward : intensity === 4 ? labelHighActivity : labelActivity
  return (
    <div
      className="heat-cell"
      data-h={intensity}
      title={`${hour.toString().padStart(2, '0')}:00 — ${cellLabel}`}
      style={{ height: 20 }}
    />
  )
}

// --- HotspotHeatmapCard ---

export function HotspotHeatmapCard({ hourlyRewards }: Props) {
  const { t } = useTranslation('hotspots')
  const hasData = Array.isArray(hourlyRewards) && hourlyRewards.length === 24

  // Intensität aus echten Daten berechnen (normalisiert 0–4). Kein Math.random().
  const cells = useMemo(() => {
    if (!hasData) return []
    const max = Math.max(...hourlyRewards!, 1)
    return hourlyRewards!.map((reward, hour) => ({
      hour,
      intensity: Math.min(4, Math.floor((reward / max) * 5)),
    }))
  }, [hourlyRewards, hasData])

  return (
    <div className="card card-pad" aria-label={t('heatmap.cardLabel')}>
      <h3 className="section-heading" style={{ marginBottom: 4 }}>
        {t('heatmap.heading')}
      </h3>
      <p className="muted" style={{ fontSize: 12, margin: '2px 0 14px' }}>
        {t('heatmap.subheading')}
      </p>

      {!hasData ? (
        <div
          className="muted"
          style={{ fontSize: 13, padding: '24px 0', textAlign: 'center' }}
          data-testid="heatmap-empty-state"
        >
          {t('heatmap.emptyMsg')}
        </div>
      ) : (
        <>
          <div
            className="heat-grid"
            role="img"
            aria-label={t('heatmap.gridLabel')}
          >
            {cells.map(({ hour, intensity }) => (
              <HeatCell
                key={hour}
                hour={hour}
                intensity={intensity}
                labelNoReward={t('heatmap.cellNoReward')}
                labelHighActivity={t('heatmap.cellHighActivity')}
                labelActivity={t('heatmap.cellActivity')}
              />
            ))}
          </div>

          <div
            className="row"
            style={{ marginTop: 10, fontSize: 11, justifyContent: 'space-between' }}
          >
            <span className="muted">00</span>
            <span className="muted">06</span>
            <span className="muted">12</span>
            <span className="muted">18</span>
            <span className="muted">23</span>
          </div>
        </>
      )}
    </div>
  )
}
